import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Card, Button, Input, Select, DatePicker, Space, Tag, Tooltip,
  message, Modal, Row, Col, Typography, Divider, Badge, Switch
} from 'antd';
import {
  ReloadOutlined, FilterOutlined, InfoCircleOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import logger from '../../services/logger';
import { safeFormatTimestamp } from '../../utils/timezone';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;

// Log seviyeleri - DB gerçek değerler
const LOG_LEVEL_OPTIONS = [
  { value: 'info', label: 'Info', color: 'processing' },
  { value: 'warning', label: 'Warning', color: 'warning' },
  { value: 'error', label: 'Error', color: 'error' }
];

// Log kategorileri - DB gerçek değerler
const LOG_CATEGORY_OPTIONS = [
  { value: 'websocket', label: 'WebSocket', color: 'purple' },
  { value: 'auth', label: 'Auth', color: 'blue' },
  { value: 'system', label: 'Sistem', color: 'green' }
];

// Log tipleri - DB gerçek değerler (en yaygınlar)
const LOG_TYPE_OPTIONS = [
  { value: 'message', label: 'Mesaj', color: 'default' },
  { value: 'request', label: 'İstek', color: 'blue' },
  { value: 'read', label: 'Okuma', color: 'cyan' },
  { value: 'login', label: 'Giriş', color: 'green' },
  { value: 'login_failed', label: 'Başarısız Giriş', color: 'red' },
  { value: 'create', label: 'Oluştur', color: 'blue' },
  { value: 'update', label: 'Güncelle', color: 'orange' },
  { value: 'delete', label: 'Sil', color: 'red' },
  { value: 'deploy', label: 'Deploy', color: 'purple' },
  { value: 'connect', label: 'Bağlan', color: 'green' },
  { value: 'disconnect', label: 'Kopma', color: 'volcano' }
];

// Kaynak - DB gerçek değerler
const SOURCE_OPTIONS = [
  { value: 'websocket', label: 'WebSocket', color: 'purple' },
  { value: 'api', label: 'API', color: 'blue' },
  { value: 'system', label: 'Sistem', color: 'green' },
  { value: 'security', label: 'Güvenlik', color: 'red' }
];

const ApplicationLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(10);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: pagination.pageSize,
        offset: (pagination.current - 1) * pagination.pageSize
      };
      if (filters.log_level) params.log_level = filters.log_level;
      if (filters.log_category) params.log_category = filters.log_category;
      if (filters.log_type) params.log_type = filters.log_type;
      if (filters.source) params.source = filters.source;
      if (filters.success !== undefined && filters.success !== null) params.success = filters.success;
      if (filters.start_time) params.start_time = filters.start_time;
      if (filters.end_time) params.end_time = filters.end_time;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get('/api/v1/logs/application/logs', { params });
      setLogs(response.data.logs || []);
      setPagination(prev => ({ ...prev, total: response.data.total_count || 0 }));
    } catch (error) {
      logger.error('Logs fetch error:', error);
      message.error('Loglar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, pagination.current, filters, searchTerm]);

  const fetchStatistics = useCallback(async () => {
    try {
      const params = {};
      if (filters.start_time) params.start_time = filters.start_time;
      if (filters.end_time) params.end_time = filters.end_time;
      const response = await axios.get('/api/v1/logs/application/logs/statistics', { params });
      if (response.data) setStatistics(response.data);
    } catch (error) {
      logger.error('Stats fetch error:', error);
    }
  }, [filters.start_time, filters.end_time]);

  useEffect(() => {
    fetchLogs();
    fetchStatistics();
  }, [fetchLogs, fetchStatistics]);

  // Auto-refresh
  useEffect(() => {
    let intervalId;
    if (autoRefresh) {
      intervalId = setInterval(() => { fetchLogs(); fetchStatistics(); }, refreshInterval * 1000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [autoRefresh, refreshInterval, fetchLogs, fetchStatistics]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      start_time: dates && dates[0] ? dates[0].toISOString() : null,
      end_time: dates && dates[1] ? dates[1].toISOString() : null
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getLevelConfig = (level) => LOG_LEVEL_OPTIONS.find(o => o.value === level) || { label: level, color: 'default' };
  const getCategoryConfig = (cat) => LOG_CATEGORY_OPTIONS.find(o => o.value === cat) || { label: cat, color: 'default' };
  const getTypeConfig = (type) => LOG_TYPE_OPTIONS.find(o => o.value === type) || { label: type, color: 'default' };
  const getSourceConfig = (src) => SOURCE_OPTIONS.find(o => o.value === src) || { label: src, color: 'default' };

  const columns = [
    {
      title: 'Zaman',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 170,
      fixed: 'left',
      render: (ts) => safeFormatTimestamp(ts),
      sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    },
    {
      title: 'Seviye',
      dataIndex: 'log_level',
      key: 'log_level',
      width: 90,
      render: (level) => { const c = getLevelConfig(level); return <Tag color={c.color}>{c.label}</Tag>; },
    },
    {
      title: 'Kategori',
      dataIndex: 'log_category',
      key: 'log_category',
      width: 110,
      render: (cat) => { const c = getCategoryConfig(cat); return <Tag color={c.color}>{c.label}</Tag>; },
    },
    {
      title: 'Tip',
      dataIndex: 'log_type',
      key: 'log_type',
      width: 110,
      render: (type) => { const c = getTypeConfig(type); return <Tag color={c.color}>{c.label}</Tag>; },
    },
    {
      title: 'Mesaj',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (msg) => <Tooltip title={msg} placement="topLeft"><Text ellipsis>{msg}</Text></Tooltip>,
    },
    {
      title: 'Kullanıcı',
      dataIndex: 'username',
      key: 'username',
      width: 110,
      render: (username, record) => username || (record.user_id ? `#${record.user_id}` : '-'),
    },
    {
      title: 'Kaynak',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (src) => { const c = getSourceConfig(src); return <Tag color={c.color}>{c.label}</Tag>; },
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130,
      render: (ip) => ip ? <Text code style={{ fontSize: 11 }}>{ip}</Text> : '-',
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <Button type="text" size="small" icon={<InfoCircleOutlined />}
          onClick={() => { setSelectedLog(record); setDetailModalVisible(true); }}
        />
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={2}>
          <InfoCircleOutlined /> Uygulama Logları
        </Typography.Title>
      </div>

      {/* Stats Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <Space size="large" wrap>
              <span>
                <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />
                <Text style={{ marginRight: 4 }}>Toplam</Text>
                <Text strong style={{ fontSize: 18 }}>{statistics.total_logs || 0}</Text>
              </span>
              <span>
                <Badge status="success" />
                <Text style={{ marginRight: 4 }}>Başarılı</Text>
                <Text strong style={{ color: '#52c41a', fontSize: 18 }}>{statistics.success_logs || 0}</Text>
              </span>
              {(statistics.error_logs || 0) > 0 && (
                <span>
                  <Badge status="error" />
                  <Text style={{ marginRight: 4 }}>Hata</Text>
                  <Text strong style={{ color: '#f5222d', fontSize: 18 }}>{statistics.error_logs}</Text>
                </span>
              )}
            </Space>
            {statistics.category_stats && (
              <Space size="small" wrap>
                {Object.entries(statistics.category_stats).map(([cat, count]) => {
                  const c = getCategoryConfig(cat);
                  return <Tag key={cat} color={c.color} style={{ margin: 0 }}>{c.label}: {count}</Tag>;
                })}
              </Space>
            )}
          </div>
        </Card>

      {/* Filters and Table */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Mesaj ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={() => fetchLogs()}
            style={{ width: 200 }}
            allowClear
          />
          <Select placeholder="Seviye" value={filters.log_level} onChange={(v) => handleFilterChange('log_level', v)} allowClear style={{ width: 120 }}>
            {LOG_LEVEL_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <Select placeholder="Kategori" value={filters.log_category} onChange={(v) => handleFilterChange('log_category', v)} allowClear style={{ width: 130 }}>
            {LOG_CATEGORY_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <Select placeholder="Tip" value={filters.log_type} onChange={(v) => handleFilterChange('log_type', v)} allowClear style={{ width: 140 }}>
            {LOG_TYPE_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <Select placeholder="Kaynak" value={filters.source} onChange={(v) => handleFilterChange('source', v)} allowClear style={{ width: 130 }}>
            {SOURCE_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <Select placeholder="Sonuç" value={filters.success} onChange={(v) => handleFilterChange('success', v)} allowClear style={{ width: 110 }}>
            <Option value={true}><Tag color="green">Başarılı</Tag></Option>
            <Option value={false}><Tag color="red">Başarısız</Tag></Option>
          </Select>
          <RangePicker placeholder={['Başlangıç', 'Bitiş']} onChange={handleDateRangeChange} format="YYYY-MM-DD" style={{ width: 260 }} />
        </div>

        {/* Action Buttons */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Space wrap>
            <Button icon={<FilterOutlined />} onClick={handleClearFilters} size="small">Temizle</Button>
            <Button icon={<ReloadOutlined />} onClick={() => { fetchLogs(); fetchStatistics(); }} loading={loading} size="small">Yenile</Button>
          </Space>
          <Space>
            <Switch checked={autoRefresh} onChange={setAutoRefresh} checkedChildren="Auto" unCheckedChildren="Auto" size="small" />
            {autoRefresh && (
              <>
                <Select value={refreshInterval} onChange={setRefreshInterval} style={{ width: 80 }} size="small">
                  <Option value={5}>5 sn</Option>
                  <Option value={10}>10 sn</Option>
                  <Option value={30}>30 sn</Option>
                  <Option value={60}>1 dk</Option>
                </Select>
                <Tag color="green" icon={<ReloadOutlined spin />}>Otomatik</Tag>
              </>
            )}
          </Space>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Toplam ${total} kayıt`,
          }}
          onChange={(p) => setPagination(p)}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Log Detayı"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedLog && (
          <div>
            <Row gutter={[16, 12]}>
              <Col span={12}>
                <Text type="secondary">Zaman</Text><br />
                <Text>{safeFormatTimestamp(selectedLog.timestamp)}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">ID</Text><br />
                <Text>{selectedLog.id}</Text>
              </Col>
              <Col span={8}>
                <Text type="secondary">Seviye</Text><br />
                {(() => { const c = getLevelConfig(selectedLog.log_level); return <Tag color={c.color}>{c.label}</Tag>; })()}
              </Col>
              <Col span={8}>
                <Text type="secondary">Kategori</Text><br />
                {(() => { const c = getCategoryConfig(selectedLog.log_category); return <Tag color={c.color}>{c.label}</Tag>; })()}
              </Col>
              <Col span={8}>
                <Text type="secondary">Tip</Text><br />
                {(() => { const c = getTypeConfig(selectedLog.log_type); return <Tag color={c.color}>{c.label}</Tag>; })()}
              </Col>
              <Col span={8}>
                <Text type="secondary">Kaynak</Text><br />
                {(() => { const c = getSourceConfig(selectedLog.source); return <Tag color={c.color}>{c.label}</Tag>; })()}
              </Col>
              <Col span={8}>
                <Text type="secondary">Sonuç</Text><br />
                {selectedLog.success != null ? (
                  <Tag color={selectedLog.success ? 'green' : 'red'}>{selectedLog.success ? 'Başarılı' : 'Başarısız'}</Tag>
                ) : '-'}
              </Col>
              <Col span={8}>
                <Text type="secondary">Kullanıcı</Text><br />
                <Text>{selectedLog.username || (selectedLog.user_id ? `#${selectedLog.user_id}` : '-')}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">IP Adresi</Text><br />
                {selectedLog.ip_address ? <Text code>{selectedLog.ip_address}</Text> : '-'}
              </Col>
              <Col span={12}>
                <Text type="secondary">User Agent</Text><br />
                <Text ellipsis style={{ maxWidth: 280 }}>{selectedLog.user_agent || '-'}</Text>
              </Col>
            </Row>
            <Divider />
            <Text type="secondary">Mesaj</Text>
            <div style={{ marginTop: 4, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              {selectedLog.message}
            </div>
            {selectedLog.details && (
              <>
                <Divider />
                <Text type="secondary">Detaylar</Text>
                <TextArea
                  value={JSON.stringify(selectedLog.details, null, 2)}
                  readOnly
                  rows={6}
                  style={{ marginTop: 4 }}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApplicationLogs;
